import { withTheme, css } from '@emotion/react';
import { withRouter } from "react-router";
import styled from '@emotion/styled';
import React, { useContext, useEffect, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useLoadItems } from './utils';
import studyKikLogo from '../../assets/images/logo-study_kik.svg';
import mapPin from '../../assets/images/icons/map-pin.svg';
import loaderImg from '../../assets/images/design_elements/loader-blue.gif';
import car from '../../assets/images/icons/car.png';
import EnvContext from '../../EnvContext';

const GUTTER = 20;

const StudyCard = styled.li`
  border: 1px solid ${props => props.theme.colors.neutral2};
  border-radius: 6px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, .2);
  cursor: pointer;
  margin-bottom: ${props => props.theme.margins.sm};
  transform: translate(0px, 0px);
  transition: all 0.5s ease;
  width: 100%;

  &:hover {
    box-shadow: 1px 10px 10px rgba(0, 0, 0, .3);
    transform: translate(0px, -5px);
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    width: calc(50% - 10px);

    &:nth-of-type(1n) {
      margin-right: ${GUTTER}px;
    }

    &:nth-of-type(2n) {
      margin-right: 0px;
    }
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    width: calc(33% - 12px);
    margin-right ${GUTTER}px;

    &:nth-of-type(2n) {
      margin-right: ${GUTTER}px;
    }

    &:nth-of-type(3n) {
      margin-right: 0px;
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    width: calc(25% - 15px);
    margin-right ${GUTTER}px;

    &:nth-of-type(3n) {
      margin-right: ${GUTTER}px;
    }

    &:nth-of-type(4n) {
      margin-right: 0px;
    }
  }
`;


const ImgWrapper = styled.div`
  height: 180px;
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 6px 6px 0 0;
`;

const ImgBlur = styled.div`
  height: 180px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  filter: blur(10px);
  position: absolute;
  z-index: -1;
  opacity: .5;
  overflow: hidden;

  ${props => !!props.bgSrc && css`
    background-image: url(${props.bgSrc});
  `}
`;

const ImgFront = styled.div`
  height: 180px;
  background-image: url(${props => !!props.bgSrc ? props.bgSrc : studyKikLogo });
  background-position: center;
  background-repeat: no-repeat;
  background-size: ${props => !!props.bgSrc ? 'contain' : '75%' };
  z-index: 1;
`;

const DesriptionContainer = styled.div`
  background: ${props => props.theme.colors.neutral2};
  padding: ${props => props.theme.margins.xxxs};
`;

const Description = styled.div`
  color: ${props => props.theme.colors.neutral1};
  font-family: ${props => props.theme.fonts.secondary};
  font-size: 14px !important;
  height: 80px;
  line-height: ${props => props.theme.lineHeights.sm};
  overflow: hidden;

  span {
    font-size: 14px !important;
  }
`;

const StyledAnchor = styled.span`
  color: ${props => props.theme.colors.primary1};
  display: block;
  font-family: ${props => props.theme.fonts.secondary};
  padding-top: ${props => props.theme.margins.xxxxs};
`;

const CardLink = styled.a`
  text-decoration: none;
`;

const Title = styled.h3`
  color: ${props => props.theme.colors.primary4};
  font-family: ${props => props.theme.fonts.primary};
  font-size:  ${props => props.theme.fontSizes.sm};
  font-weight: bold;
  padding-bottom: ${props => props.theme.margins.xxxxs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Location = styled.div`
  background-image: url(${mapPin});
  background-repeat: no-repeat;
  background-size: 12px;
  background-position: bottom  left;
  color: ${props => props.theme.colors.neutral1};
  font-family: ${props => props.theme.fonts.secondary};
  font-size:  ${props => props.theme.fontSizes.xs};
  padding-left: 18px;
  padding-top: 8px;
`;

const Distance = styled.div`
  background-image: url(${car});
  background-repeat: no-repeat;
  background-size: 12px;
  background-position: bottom 1px left;
  color: ${props => props.theme.colors.neutral1};
  font-family: ${props => props.theme.fonts.secondary};
  font-size:  ${props => props.theme.fontSizes.xs};
  padding-left: 18px;
  padding-top: 12px;
  opacity: ${props => props.isVisible ? 1 : 0}
`;

const InfoWrapper = styled.div`
  padding: ${props => props.theme.margins.xxxs};
  min-height: 103px;
`;


const ListContainer = styled.div`
  overflow: auto;
  width: 100%;
`;

const LoadingRoot = styled.div`
  background-image: url(${loaderImg});
  background-size: 50px;
  background-position-x: calc(50% - 70px);
  background-position-y: top;
  background-repeat: no-repeat;
  color: ${props => props.theme.colors.primary1};
  font-family: ${props => props.theme.fonts.secondary};
  padding: 8px;
`;

const List = styled.ul`
  display: flex;
	flex-flow: wrap;
  list-style: none;
  font-size: 16px;
  margin: 36px auto 0;
  padding: 6px;
	max-width: 1000px;
`;

const LoadItem = styled.li`
  padding: 8px;
  margin: 4px;
  display: block;
  width: 100%;
  text-align: center;
`;

const NoResults = styled.div`
  color: ${props => props.theme.colors.neutral1};
  font-family: ${props => props.theme.fonts.secondary};
  line-height: ${props => props.theme.lineHeights.sm};
  width: 100%;
  text-align: center;
`;

function renderStudies(studies, envBaseUrl) {
  return studies.map((study, i) => {
    const { name, image, study_id, url_part, city, state, landingdescription, distance, landingcoming, landinglocation } = study;
    const locationText = landingcoming && landinglocation ? landinglocation : `${city}, ${state}`;
    return (
      <StudyCard key={`${study_id}-${url_part}-${i}`} data-testid={`study-card-container-${study_id}`}>
        <CardLink href={`${envBaseUrl}/${study_id}-${url_part}`} target="_blank">
          <ImgWrapper data-testid={'study-card-images'}>
            <ImgBlur bgSrc={image} />
            <ImgFront bgSrc={image} />
          </ImgWrapper>
          <InfoWrapper>
            <Title data-testid={'study-card-title'}>{name}</Title>
            <Location data-testid={'study-card-location'}>{locationText}</Location>
            <Distance  data-testid={'study-card-distance'} isVisible={!!distance && !landingcoming}>{!!distance ? `${distance} Miles` : " "}</Distance>
          </InfoWrapper>
          <DesriptionContainer>
            <Description data-testid={'study-card-description'} dangerouslySetInnerHTML={{ __html: landingdescription }} />
            <StyledAnchor data-testid={'study-card-description-anchor'}>Learn More</StyledAnchor>
          </DesriptionContainer>
        </CardLink>
      </StudyCard>
    )
  })
}

function renderProtocolPages(protocolPages, envBaseUrl) {
  return protocolPages.map((plp, i) => {
    const { title, location, landingPageBody, imgSrc, url, uuid, city, state, distance } = plp;
    const locationText = city && state ? `${city}, ${state}` : location ? location : null;

    return (
      <StudyCard key={uuid} data-testid={`study-card-container-${uuid}`}>
        <CardLink href={`${envBaseUrl}/study-page/${url}`} target="_blank">
          <ImgWrapper data-testid={'study-card-images'}>
            <ImgBlur bgSrc={imgSrc} />
            <ImgFront bgSrc={imgSrc} />
          </ImgWrapper>
          <InfoWrapper>
            <Title data-testid={'study-card-title'}>{title}</Title>
            {locationText && <Location data-testid={'study-card-location'}>{locationText}</Location> }
            {distance && <Distance data-testid={'study-card-distance'} isVisible={!!distance}>{!!distance ? `${distance} Miles` : " "}</Distance>}
          </InfoWrapper>
          <DesriptionContainer>
            <Description data-testid={'study-card-description'} dangerouslySetInnerHTML={{ __html: landingPageBody }} />
            <StyledAnchor data-testid={'study-card-description-anchor'}>Learn More</StyledAnchor>
          </DesriptionContainer>
        </CardLink>
      </StudyCard>
    )
  })
}

function InfiniteListWithVerticalScroll(props) {
  const { envBaseUrl } = useContext(EnvContext);
  const { loading, studies, protocolLandingPages, hasNextPage: { hasMoreStudies, hasMoreProtocolPages}, error, loadMore } = useLoadItems(props);
  const hasNextPage = hasMoreProtocolPages || hasMoreStudies;
  // Prevent flash of unstyled content
  const [foucTimer, setFoucTimer] = useState(false);
  useEffect(() => {
    setTimeout(() => setFoucTimer(true), 3000);
  }, []);
  
  const [infiniteRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    disabled: !!error,
    rootMargin: '0px 0px 400px 0px',
  });

  const noResults = !loading && !studies.length && !protocolLandingPages.length;

  return (
    <ListContainer>
      <List data-testid={'study-card-list'}>
        {
          noResults && foucTimer ?
          <NoResults>No results</NoResults>
          : 
          <>
            {renderProtocolPages(protocolLandingPages, envBaseUrl)}
            {renderStudies(studies, envBaseUrl)}
          </>
        }
        {hasNextPage && (
          <LoadItem ref={infiniteRef}>
            <LoadingRoot>Loading...</LoadingRoot>
          </LoadItem>
        )}
      </List>
    </ListContainer>
  );
}

export default withRouter(withTheme(InfiniteListWithVerticalScroll));